import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import Calendar from "./Calendar";
import type { MergedCalendarDay } from "@features/booking/types/calendar";
import type { ServiceType } from "@features/booking/state/types";
import type { Locale } from "date-fns";
import { mapToDisplayType } from "@features/booking/utils/availability";

interface NoAvailabilityCalendarProps {
  mergedCalendar: MergedCalendarDay[];
  isLoading: boolean;
  onDateSelect: (date: Date, service: ServiceType) => void;
  serviceTypes: ServiceType[];
  currentService: ServiceType;
  locale?: Locale;
}

/**
 * Derive per-service calendar from merged data.
 * For each day, use the specific service's type instead of the merged (OR) type.
 */
function getServiceCalendar(
  mergedCalendar: MergedCalendarDay[],
  serviceType: ServiceType,
): MergedCalendarDay[] {
  return mergedCalendar.map((day) => {
    const serviceData = day.services[serviceType];
    if (!serviceData) {
      // This service has no data for this date — closed
      return { ...day, displayType: 'closed' as const, type: 'not-available-closed' as const };
    }
    return {
      ...day,
      displayType: mapToDisplayType(serviceData.type),
      type: serviceData.type,
      isSpecialDay: serviceData.isSpecialDay,
      highlightSpecialDay: serviceData.highlightSpecialDay,
    };
  });
}

const NoAvailabilityCalendar = ({
  mergedCalendar,
  isLoading,
  onDateSelect,
  serviceTypes,
  currentService,
  locale,
}: NoAvailabilityCalendarProps) => {
  const { t } = useTranslation('booking');
  const [selectedService, setSelectedService] = useState<ServiceType>(currentService);

  const hasMultipleServices = serviceTypes.length > 1;

  // Derive per-service calendar data
  const calendarData = hasMultipleServices
    ? getServiceCalendar(mergedCalendar, selectedService)
    : mergedCalendar;

  return (
    <div className="mt-8">
      <div className="border-t border-border w-full mb-6" />

      {/* Header with service selector */}
      <h3 className="text-center text-foreground font-medium text-lg mb-4">
        {hasMultipleServices ? (
          <>
            {t('AVAILABILITY_CALENDAR_FOR')}{' '}
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value as ServiceType)}
              className="inline-block border border-border rounded-md px-2 py-1 text-brand font-medium bg-card cursor-pointer"
            >
              {serviceTypes.map((st) => (
                <option key={st} value={st}>
                  {t(`FILTER_STRING_${st.toUpperCase()}`, st)}
                </option>
              ))}
            </select>
          </>
        ) : (
          t('AVAILABILITY_CALENDAR')
        )}
      </h3>

      {/* Clamp the calendar to `max-w-md` (448px) so its proportions match
          FullCalendarPage exactly — same wrapper width, same Calendar
          component, identical cell sizing/font/spacing. The parent
          SelectTimeMain wraps this whole component in `max-w-2xl`, but
          that breadth is for the header + legend; the calendar grid
          itself needs the tighter cap or cells balloon and the chrome
          feels off relative to the rest of the booking flow. */}
      <div className="max-w-md mx-auto">
        <Calendar
          onChange={(date) => onDateSelect(date, selectedService)}
          availability={calendarData}
          isLoading={isLoading}
          locale={locale}
        />
      </div>

      {/* Legend — only explains the exceptions. "Available" is the implicit
          default and doesn't need a swatch. Centered within the same
          max-w-md so it visually anchors to the calendar above it. */}
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 text-xs text-foreground/70 max-w-md mx-auto">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
          {t('CALENDAR_LEGEND_WAITING_LIST')}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
          {t('CALENDAR_LEGEND_WALK_IN')}
        </div>
        <div className="flex items-center gap-1.5">
          <Star size={12} className="text-amber-400 fill-amber-300" />
          {t('CALENDAR_LEGEND_EVENT')}
        </div>
      </div>
    </div>
  );
};

export default NoAvailabilityCalendar;
